import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Button} from '@/components/ui/button/Button'
import {
    Dialog,
    DialogDisclosure,
    DialogDismiss,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Download01Icon} from '@/components/ui/icon/Icon'
import {
    StyledExportSiarDialogBody,
    StyledExportSiarDialogFooter
} from '@/features/projects/components/export-siar-dialog/style'
import {ExportSiarTrigger} from '@/features/projects/components/export-siar-trigger/ExportSiarTrigger'
import {ProjectIndex} from '@/features/projects/types'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useTranslation} from '@/translations/i18n'
import {useDialogContext, useDialogStore} from '@ariakit/react'
import {ComponentType, MouseEvent, useMemo, useState} from 'react'

export const ExportSiarDialog = ({
    projects,
    customTrigger: CustomTrigger
}: {
    projects: ProjectIndex[]
    customTrigger?: ComponentType<{onClick: (event?: MouseEvent<HTMLElement>) => void; isDisabled: boolean}>
}) => {
    const {t} = useTranslation()
    const [isOpen, setIsOpen] = useState(false)

    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: setIsOpen
    })

    // const isEnabled = useMemo(() => {
    //     return projects.every(
    //         project =>
    //             (project.status == 'open' && project.progressStatus == 'to_export') ||
    //             (project.status == 'open' && project.progressStatus == 'to_transmit') ||
    //             (project.status == 'closed' && project.progressStatus == 'to_transmit')
    //     )
    // }, [projects])

    // Just for testing
    const isEnabled = useMemo(() => {
        return projects.every(
            project => project.progressStatus == 'to_export' || project.progressStatus == 'to_transmit'
        )
    }, [projects])

    return (
        <DialogProvider store={dialogStore}>
            <DialogDisclosure
                render={
                    CustomTrigger ? (
                        <CustomTrigger onClick={() => setIsOpen(true)} isDisabled={!isEnabled} />
                    ) : (
                        <Button onClick={() => setIsOpen(true)} disabled={!isEnabled}>
                            <Download01Icon />
                            {t('projects:export_siar')}
                        </Button>
                    )
                }
            />
            <Dialog width="288px">
                <ExportSiarDialogContent projects={projects} />
            </Dialog>
        </DialogProvider>
    )
}

const ExportSiarDialogContent = ({projects}: {projects: ProjectIndex[]}) => {
    const {t} = useTranslation()
    const [addRegistry, setAddRegistry] = useState(false)

    const dialog = useDialogContext()

    const exportSiarTypeToExport = useMemo(() => {
        return projects.every(project => project.status == 'open')
            ? 'open_project'
            : projects.every(project => project.status == 'closed')
              ? 'close_project'
              : null
    }, [projects])

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('projects:export_siar_xml')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <StyledExportSiarDialogBody>
                <Checkbox
                    id="export-siar-anagraphic"
                    label={t('projects:export_siar_anagraphic')}
                    checked={addRegistry}
                    onChange={() => setAddRegistry(!addRegistry)}
                />
            </StyledExportSiarDialogBody>
            <StyledExportSiarDialogFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                <ExportSiarTrigger
                    exportSiarType={exportSiarTypeToExport}
                    projectIds={projects.map(project => project.id)}
                    addRegistry={addRegistry}
                    onExportSuccess={() => {
                        void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                        void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
                        projects.forEach(project => {
                            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, project.id]})
                        })
                        dialog?.setOpen(false)
                    }}
                />
            </StyledExportSiarDialogFooter>
        </>
    )
}
